import React, { useState, useEffect, useRef } from "react";
import { filterBy } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import SubmitButton from "components/common/SubmitButton";
import { useSelector, useDispatch } from "react-redux";
import { FILES_CONTEXT, useFiles } from "services/filesDownloadService";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  clearMatterBalances,
  fetchMatterBalances,
  fetchMatterDetails,
} from "services/mattersService";
import {
  getChitRequest,
  updateChitRequest,
  createChitRequest as createChitRequestAPI,
  getMattersdetails,
  getMatterDefaults,
} from "api";
import { rejectChitRequest, getChitDocuments } from "api/accountsAPI";
import { toast } from "react-toastify";
import { useUploadForm } from "components/fileUploadHook";
import { onFileUpload } from "services/fileDataService";
import { useActiveFeeEarner } from "services/userService";
import { useActiveVats } from "services/vatService";
import {
  getPostingTypes,
  getCachedPostingTypes,
} from "services/postingTypesService";
import { useActiveSuppliers } from "services/supplierService";
import { useNominalsBySubType } from "services/nominalsService";
import { useActiveBranches } from "services/branchService";
import { useCompany } from "services/companyService";
import chitRequestFieldsConfig from "./chit_request_fields.json";
import { formatDate } from "@progress/kendo-intl";
import ChitRequestFormContent from "./ChitRequestFormContent";
import ChitRequestRejectDialog from "./ChitRequestRejectDialog";
import { formatDateForAPI } from "components/common/formatter";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { useAccessRight } from "components/UserAccessRights.ts";
import { CaseAccessRights } from "components/UserAccessRights.ts";
import {
  getChitValidationErrors,
  isChitNetAmountInvalid,
} from "./chitRequestValidation";

const buildRejectionDisplay = (src) => {
  if (!src) return null;
  const reason = src.rejectReason ?? "";
  const dateStr = src.dateRejected
    ? formatDate(new Date(src.dateRejected), "d", "en-GB")
    : "";
  const ref = src.rejectedByUserRef ?? "";
  const line1 = dateStr
    ? `${dateStr} - Rejected by '${ref}'`
    : `Rejected by '${ref}'`;
  return { line1, reason };
};

const isPayeeSideInvalid = (list, payee, payer) => {
  if (list.includes("Payee")) {
    return !String(payee || "").trim();
  }
  if (list.includes("Payer")) {
    return !String(payer || "").trim();
  }
  return false;
};

const normalizeFields = (postingCode) => {
  const postingType = chitRequestFieldsConfig.data.find(
    (pt) => pt.code === postingCode,
  );

  if (!postingType) return { list: [] };
  const displayFields = postingType.displayFields;

  // Build list directly from JSON
  const list = displayFields.map((f) => f.name);

  return { list };
};

const ChitRequest = (props) => {
  const { data: company } = useCompany();
  const IS_MODAL = props.component === "modal";
  const activityData = props.activityData ?? {};
  const chitRequestId =
    props.chitRequestId ??
    activityData.chitRequestId ??
    activityData.chitId ??
    activityData.subTypeId;
  const unAllocated = Boolean(props.unAllocated ?? activityData.unallocatedOnly);
  const isChitRequestPosted =
    Boolean(chitRequestId) &&
    String(activityData?.subTypeStatus ?? "").toLowerCase() === "posted";
  const chitRequestReadOnly = isChitRequestPosted;
  const resolvedMatterId =
    props.matterId ?? props.matter?.matterId ?? activityData.matterID ?? null;
  const viewMode =
    props.viewMode ??
    (chitRequestId
      ? "view"
      : unAllocated
        ? "unallocated"
        : IS_MODAL
          ? "dialog"
          : "add");
  const IS_UNALLOCATED_CREATE = IS_MODAL && unAllocated && !chitRequestId;
  const canSelectMatterInModal = IS_MODAL && unAllocated && !resolvedMatterId;
  const cachedPostingTypes = getCachedPostingTypes(props.postingGroup || "chits", {
    unallocated: IS_UNALLOCATED_CREATE,
  });
  const seedPostingType =
    !chitRequestId && cachedPostingTypes?.length ? cachedPostingTypes[0] : null;

  const [list, setList] = useState(
    () => (seedPostingType ? normalizeFields(seedPostingType.code).list : []),
  );
  const { data: activevatsdata = [] } = useActiveVats();
  const [loading, setLoading] = useState(false);
  const [resetDocs, setresetDocs] = useState(false);
  const { data: feeearnerdata } = useActiveFeeEarner();
  const [postingTypes, setPostingTypes] = useState(cachedPostingTypes || []);

  const { data: expenseNominals } = useNominalsBySubType("ProfitAndLoss_Expense", {
    excludeArchived: true,
  });
  const { data: officeBankNominals } = useNominalsBySubType(
    "BalanceSheet_OfficeBank",
    { excludeArchived: true },
  );
  const { data: clientBankNominals } = useNominalsBySubType(
    "BalanceSheet_ClientBank",
    { excludeArchived: true },
  );
  const { data: ddaBankNominals } = useNominalsBySubType("BalanceSheet_DDABank", {
    excludeArchived: true,
  });
  const { data: branchesRaw = [] } = useActiveBranches();
  const date = new Date();
  const [type, settype] = useState(seedPostingType || "");
  const [vatvalue, setvatvalue] = useState();
  const [matterref, setmatterref] = useState(resolvedMatterId);
  const [feeearnerref, setfeeearnerref] = useState();
  const [matterBalanceClear, setMatterBalanceClear] = useState(false);
  const [matter, setMatter] = useState(
    props.matter ?? (resolvedMatterId ? { matterId: resolvedMatterId } : null),
  );
  const [branch, setbranch] = useState({ branchId: "" });
  const [nominal, setnominal] = useState(null);
  const [suppliervalue, setsuppliervalue] = useState(null);
  const [officevalue, setofficevalue] = useState("");
  const [clientvalue, setclientvalue] = useState("");
  const [ddavalue, setddavalue] = useState("");
  const [matterrefTo, setMatterrefTo] = useState(null);
  const [matterTo, setMatterTo] = useState(null);
  const [clientBankFrom, setClientBankFrom] = useState(null);
  const [clientBankTo, setClientBankTo] = useState(null);
  const [officeBankTo, setOfficeBankTo] = useState(null);
  const [officeBankData, setOfficeBankData] = useState([]);
  const [clientBankData, setClientBankData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [state, setstate] = useState("");
  const [payee, setpayee] = useState("");
  const [payer, setpayer] = useState("");
  const [vatamount, setvatamount] = useState();
  const [netamount, setnetamount] = useState(0);
  const [details, setdetails] = useState("");
  const [bankName, setBankName] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [dateselector, setdateselector] = useState(date);
  const { data: suppliersdata } = useActiveSuppliers();
  const [counselfee, setcounselfee] = useState(false);
  const [urgent, seturgent] = useState(false);
  const [validation_message, setvalidation_message] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejectedChit, setIsRejectedChit] = useState(
    Boolean(activityData?.rejected || activityData?.isRejected),
  );
  const [chitStatus, setChitStatus] = useState(null);
  const [rejectionInfo, setRejectionInfo] = useState(null);
  const [vatsdata, setvatsData] = useState([]);
  const [feeearner, setfeeearnerData] = useState([]);
  const [suppliers, setsuppliersdata] = useState([]);
  // Initialize loading state based on whether we need to fetch chit data
  const [isLoadingChitData, setIsLoadingChitData] = useState(
    IS_MODAL && Boolean(chitRequestId),
  );
  const [postingTypesLoaded, setPostingTypesLoaded] = useState(
    Boolean(cachedPostingTypes?.length),
  );
  const lastBalancesMatterIdRef = useRef(null);
  const matterDefaultsCacheRef = useRef({});

  const mattersBalances = useSelector((state) => state.mattersList);

  const fetchData = async () => {
    try {
      const groupToUse = props.postingGroup || "chits";
      const postingData = await getPostingTypes(groupToUse, {
        unallocated: IS_UNALLOCATED_CREATE,
      });
      if (!Array.isArray(postingData) || postingData.length === 0) return;
      if (!chitRequestId && !seedPostingType) {
        const defaultType = postingData[0];
        settype(defaultType);
        const { list } = normalizeFields(defaultType.code);
        setList(list);
      }
      setPostingTypes(postingData);
    } catch (error) {
      console.error("Failed to fetch posting types:", error);
    } finally {
      setPostingTypesLoaded(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, [props.postingGroup, IS_UNALLOCATED_CREATE, chitRequestId]);

  useEffect(() => {
    setLoading(true);
    if (
      list?.length > 0 &&
      IS_MODAL &&
      mattersBalances?.mattersChitRequest?.data?.matterRef !== undefined
    ) {
      setLoading(false);
    } else if (list?.length > 0) {
      setLoading(false);
    }
  }, [list]);

  const dispatch = useDispatch();

  const onChangesupplier = (event) => {
    if (event !== undefined && event !== null) {
      setsuppliervalue(event.value);
    }
  };
  useEffect(() => {
    if (resolvedMatterId) {
      setmatterref(resolvedMatterId);
      setMatter((prev) => {
        if (prev?.matterId === resolvedMatterId) return prev;
        return { matterId: resolvedMatterId };
      });
      if (lastBalancesMatterIdRef.current !== resolvedMatterId) {
        dispatch(
          fetchMatterBalances({
            matterId: resolvedMatterId,
            component: "Chit Request",
          }),
        );
        lastBalancesMatterIdRef.current = resolvedMatterId;
      }
    } else {
      lastBalancesMatterIdRef.current = null;
      setmatterref(null);
      setMatter((prev) => (prev?.matterId ? null : prev));
    }
  }, [resolvedMatterId, props.matter?.matterId, dispatch]);

  useEffect(() => {
    if (props.matter?.matterId && props.matter.matterId === matterref) {
      setMatter((prev) => {
        if (!prev) return props.matter;
        if (prev.details || prev.clientName || prev.branch || prev.feeEarnerName) {
          return prev;
        }
        return props.matter;
      });
    }
  }, [props.matter, matterref]);

  // Fetch matter details if missing (for edit mode from ChitPosting)
  useEffect(() => {
    const fetchMatterDetails = async () => {
      if (!matter?.matterId) return;

      // Check if matter details are incomplete
      const isMatterIncomplete =
        !matter.details ||
        !matter.clientName ||
        !matter.branch ||
        !matter.feeEarnerName;

      if (isMatterIncomplete) {
        try {
          const response = await getMattersdetails(matter.matterId);
          setMatter((prevMatter) => ({ ...prevMatter, ...response.data?.data }));
        } catch (error) {
          console.error("Error fetching matter details:", error);
        }
      }
    };

    fetchMatterDetails();
  }, [matter?.matterId, chitRequestId, resolvedMatterId]);

  // BatchPosting pattern: populate defaults from existing data (like handleUpdate)
  const populateDefaultsFromChit = (chitData) => {
    if (!chitData) return;
    setIsRejectedChit(Boolean(chitData.rejected || chitData.isRejected));
    if (chitData.status) setChitStatus(chitData.status);
    if (chitData.rejected || chitData.isRejected) {
      setRejectionInfo(buildRejectionDisplay(chitData));
    } else {
      setRejectionInfo(null);
    }

    // Load matter and balances first (like BatchPosting handleUpdate)
    if (chitData.matterID) {
      const matterId = chitData.matterID;
      setmatterref(matterId);
      setMatter((prev) =>
        prev?.matterId === matterId ? prev : { matterId: matterId },
      );
      dispatch(fetchMatterDetails(matterId));
      dispatch(fetchMatterBalances({ matterId, component: "Chit Request" }));
    }

    // Set all form fields from existing data (BatchPosting pattern)
    chitData.details && setdetails(chitData.details);
    chitData.date && setdateselector(new Date(chitData.date));
    chitData.reference && setstate(chitData.reference);
    chitData.netAmount !== undefined && setnetamount(chitData.netAmount);
    chitData.vatAmount !== undefined && setvatamount(chitData.vatAmount);
    chitData.payee && setpayee(chitData.payee);
    chitData.payer && setpayer(chitData.payer);
    chitData.bankName && setBankName(chitData.bankName);
    chitData.bankSortCode && setSortCode(chitData.bankSortCode);
    chitData.bankAccountNo && setAccountNumber(chitData.bankAccountNo);
    chitData.notes && setNotes(chitData.notes);
    chitData.isCounselFee !== undefined && setcounselfee(chitData.isCounselFee);
    chitData.urgent !== undefined && seturgent(Boolean(chitData.urgent));

    // Set complex objects
    chitData.supplierRef && setsuppliervalue({ supplierRef: chitData.supplierRef });
    chitData.nominalRef && setnominal({ nominalRef: chitData.nominalRef });
    chitData.branchId && setbranch({ branchId: chitData.branchId });
    chitData.officeBank && setofficevalue({ nominalRef: chitData.officeBank });
    if (chitData.clientBank) {
      if (chitData.postingType === "CTC") {
        setClientBankFrom({ nominalRef: chitData.clientBank });
      } else {
        setclientvalue({ nominalRef: chitData.clientBank });
      }
    }
    chitData.ddaBank && setddavalue({ nominalRef: chitData.ddaBank });
    chitData.matterIDTo && setMatterrefTo(chitData.matterIDTo);
    chitData.matterIDTo &&
      setMatterTo({
        matterId: chitData.matterIDTo,
        matterRef: chitData.matterRefTo,
      });
    chitData.clientBankTo && setClientBankTo({ nominalRef: chitData.clientBankTo });
    chitData.officeBankTo && setOfficeBankTo({ nominalRef: chitData.officeBankTo });

    // Store references for later processing when data becomes available
    chitData.feeEarnerRef && setfeeearnerref(chitData.feeEarnerRef);

    // Set VAT code
    if (chitData.vatCode && activevatsdata?.length > 0) {
      const vatValue = activevatsdata.find(
        (item) => item.vatCodeId === chitData.vatCode,
      );
      if (vatValue) setvatvalue(vatValue);
    }

    // Set posting type and update visible fields
    if (chitData.postingType && postingTypes?.length > 0) {
      const matchingPostingType = postingTypes.find(
        (pt) => pt.code === chitData.postingType,
      );
      if (matchingPostingType) {
        settype(matchingPostingType);
        const { list } = normalizeFields(matchingPostingType.code);
        setList(list);
      }
    }
  };

  useEffect(() => {
    setIsRejectedChit(Boolean(activityData?.rejected || activityData?.isRejected));
    if (activityData?.rejected || activityData?.isRejected) {
      setRejectionInfo(buildRejectionDisplay(activityData));
    } else {
      setRejectionInfo(null);
    }
  }, [activityData]);

  // Handle prefilling data from activity (for edit mode) - BatchPosting pattern
  useEffect(() => {
    const fetchAndPrefillChitData = async () => {
      if (!IS_MODAL) return;

      // Wait for posting types to settle before fetching chit data
      if (!postingTypesLoaded) return;
      if (postingTypes.length === 0) {
        setIsLoadingChitData(false);
        return;
      }

      if (IS_UNALLOCATED_CREATE) {
        setIsLoadingChitData(false);
        return;
      }

      if (chitRequestId) {
        try {
          const response = await getChitRequest(chitRequestId);
          const chitData = response.data?.data;
          if (chitData) {
            // Use BatchPosting pattern - populate all defaults from existing data
            populateDefaultsFromChit(chitData);
          }
        } catch (error) {
          console.error("Error fetching chit data:", error);
        } finally {
          setIsLoadingChitData(false);
        }
      } else {
        // Fallback: prefill with basic activity data if no chitRequestId
        const basicData = {
          details: activityData.details,
          date: activityData.date,
          feeEarnerRef: activityData.feeEarnerRef,
        };
        populateDefaultsFromChit(basicData);
        setIsLoadingChitData(false);
      }
    };

    fetchAndPrefillChitData();
  }, [
    chitRequestId,
    IS_UNALLOCATED_CREATE,
    IS_MODAL,
    postingTypes,
    postingTypesLoaded,
    activityData.details,
    activityData.date,
    activityData.feeEarnerRef,
  ]);

  useEffect(() => {
    if (
      feeearnerdata?.length > 0 &&
      feeearnerref &&
      typeof feeearnerref === "string"
    ) {
      // Convert feeEarnerRef string to feeEarner object
      const matchingFeeEarner = feeearnerdata.find(
        (fe) => fe.userRef === feeearnerref,
      );
      if (matchingFeeEarner) {
        setfeeearnerref(matchingFeeEarner);
      }
    }
  }, [feeearnerdata, feeearnerref]);

  useEffect(() => {
    const matterId = matter?.matterId;
    if (IS_MODAL && matterId && !chitRequestId && !vatvalue && !feeearnerref) {
      if (
        activevatsdata?.length > 0 ||
        feeearnerdata?.length > 0 ||
        officeBankNominals?.length > 0 ||
        clientBankNominals?.length > 0 ||
        ddaBankNominals?.length > 0
      ) {
        applyMatterDefaults(matterId);
      }
    }
  }, [
    matter?.matterId,
    IS_MODAL,
    chitRequestId,
    Boolean(vatvalue),
    Boolean(feeearnerref),
    activevatsdata?.length,
    feeearnerdata?.length,
    officeBankNominals?.length,
    clientBankNominals?.length,
    ddaBankNominals?.length,
  ]);

  useEffect(() => {
    if (
      activevatsdata?.length > 0 &&
      vatvalue &&
      typeof vatvalue.vatCodeId === "number"
    ) {
      // Find matching VAT object from the VAT data array
      const matchingVat = activevatsdata.find(
        (vat) => vat.vatCodeId === vatvalue.vatCodeId,
      );
      if (matchingVat && !vatvalue.display) {
        // Update with full VAT object if we only have the ID and rate
        setvatvalue({ ...vatvalue, ...matchingVat });
      }
    }
  }, [activevatsdata, vatvalue]);

  const handleChange = async (event) => {
    setvalidation_message(false);
    settype(event.value);

    const { list } = normalizeFields(event.value.code);

    setList(list);

    // Apply matter defaults when posting type changes (BatchPosting pattern)
    if (matterref && !chitRequestId) {
      await applyMatterDefaults(matterref);
    }
  };

  useEffect(() => {
    setvatsData(activevatsdata?.slice?.() || []);
  }, [activevatsdata]);

  useEffect(() => {
    setfeeearnerData(feeearnerdata?.slice?.() || []);
  }, [feeearnerdata]);

  useEffect(() => {
    setsuppliersdata(suppliersdata?.slice?.() || []);
  }, [suppliersdata]);

  useEffect(() => {
    setOfficeBankData(officeBankNominals?.slice?.() || []);
  }, [officeBankNominals]);

  useEffect(() => {
    setClientBankData(clientBankNominals?.slice?.() || []);
  }, [clientBankNominals]);

  useEffect(() => {
    setBranchesData(branchesRaw?.slice?.() || []);
  }, [branchesRaw]);

  const supplierRefForLookup = suppliervalue?.supplierRef;
  useEffect(() => {
    if (!supplierRefForLookup || !suppliersdata?.length) return;
    const match = suppliersdata.find(
      (supplier) => supplier.supplierRef === supplierRefForLookup,
    );
    if (match && match !== suppliervalue) setsuppliervalue(match);
  }, [supplierRefForLookup, suppliersdata]);

  const nominalRefForLookup = nominal?.nominalRef;
  useEffect(() => {
    if (!nominalRefForLookup || !expenseNominals?.length) return;
    const match = expenseNominals.find(
      (item) => item.nominalRef === nominalRefForLookup,
    );
    if (match && match !== nominal) setnominal(match);
  }, [nominalRefForLookup, expenseNominals]);

  const branchIdForLookup = branch?.branchId;
  useEffect(() => {
    if (branchIdForLookup === undefined || !branchesRaw?.length) return;
    const match = branchesRaw.find(
      (item) => String(item.branchId) === String(branchIdForLookup),
    );
    if (match && match !== branch) setbranch(match);
  }, [branchIdForLookup, branchesRaw]);

  const officeBankRefForLookup = officevalue?.nominalRef;
  useEffect(() => {
    const code = type?.code || type;
    if (
      !["BP", "BR"].includes(code) ||
      !officeBankRefForLookup ||
      !officeBankNominals?.length
    ) {
      return;
    }

    const match = officeBankNominals.find(
      (item) => item.nominalRef === officeBankRefForLookup,
    );
    if (match) {
      if (match !== officevalue) setofficevalue(match);
      return;
    }

    setofficevalue(null);
  }, [type, officeBankRefForLookup, officeBankNominals]);

  // fetch matter defaults once per matterId
  const fetchMatterDefaults = (matterId) => {
    const cache = matterDefaultsCacheRef.current;
    if (!cache[matterId]) {
      cache[matterId] = getMatterDefaults(matterId)
        .then((response) => response?.data?.data)
        .catch((error) => {
          delete cache[matterId];
          throw error;
        });
    }
    return cache[matterId];
  };

  // BatchPosting pattern: Apply matter defaults from API
  const applyMatterDefaults = async (matterId) => {
    if (!matterId) return;
    try {
      const defaults = await fetchMatterDefaults(matterId);

      if (!defaults) return;

      // Apply VAT defaults - always set (either matched value or null)
      if (defaults.defaultVatCodeId && activevatsdata?.length > 0) {
        const matchedVat = activevatsdata.find(
          (v) => v.vatCodeId === defaults.defaultVatCodeId,
        );
        setvatvalue(matchedVat || null);
      } else {
        setvatvalue(null);
      }

      // Apply Fee Earner defaults - always set (either matched value or null)
      if (defaults.matterFeeEarner && feeearnerdata?.length > 0) {
        const matchedFeeEarner = feeearnerdata.find(
          (fe) => fe.userRef === defaults.matterFeeEarner,
        );
        setfeeearnerref(matchedFeeEarner || null);
      } else {
        setfeeearnerref(null);
      }

      // Apply Office Bank defaults - always set (either matched value or null)
      if (defaults.defaultOfficeBank && officeBankNominals?.length > 0) {
        const matchedOfficeNominal = officeBankNominals.find(
          (n) => n.nominalRef === defaults.defaultOfficeBank,
        );
        setofficevalue(
          matchedOfficeNominal
            ? {
              nominalRef: defaults.defaultOfficeBank,
              display: matchedOfficeNominal.display,
            }
            : null,
        );
      } else {
        setofficevalue(null);
      }

      // Apply Client Bank defaults - always set (either matched value or null)
      if (defaults.defaultClientBank && clientBankNominals?.length > 0) {
        const matchedClientNominal = clientBankNominals.find(
          (n) => n.nominalRef === defaults.defaultClientBank,
        );
        setclientvalue(
          matchedClientNominal
            ? {
              nominalRef: defaults.defaultClientBank,
              display: matchedClientNominal.display,
            }
            : null,
        );
      } else {
        setclientvalue(null);
      }

      // Apply DDA Bank defaults - always set (either matched value or null)
      if (defaults.defaultDDABank && ddaBankNominals?.length > 0) {
        const matchedDDANominal = ddaBankNominals.find(
          (n) => n.nominalRef === defaults.defaultDDABank,
        );
        setddavalue(
          matchedDDANominal
            ? {
              nominalRef: defaults.defaultDDABank,
              display: matchedDDANominal.display,
            }
            : null,
        );
      } else {
        setddavalue(null);
      }
    } catch (error) {
      console.error("Error applying matter defaults:", error);
    }
  };

  const onChangematter = async (e) => {
    if (IS_MODAL && !canSelectMatterInModal) {
      return;
    }
    setMatterBalanceClear(false);
    const selectedMatter = e.value;
    const selectedMatterId =
      typeof selectedMatter === "object" ? selectedMatter?.matterId : selectedMatter;
    if (selectedMatterId) {
      if (typeof selectedMatter === "object") {
        setMatter(selectedMatter);
      } else {
        setMatter((prev) =>
          prev?.matterId === selectedMatterId
            ? prev
            : { matterId: selectedMatterId },
        );
      }
      dispatch(fetchMatterDetails(selectedMatterId)); // Fetch full matter details
      dispatch(
        fetchMatterBalances({
          matterId: selectedMatterId,
          component: "Chit Request",
        }),
      );
      setmatterref(selectedMatterId);

      // Apply matter defaults from API (BatchPosting pattern)
      if (!chitRequestId) {
        await applyMatterDefaults(selectedMatterId);
      }
    } else {
      setmatterref(null);
      setMatter(null);
      setMatterBalanceClear(true);
      dispatch(clearMatterBalances("Chit Request"));
    }
  };

  const filterData = (filter) => {
    const datas = activevatsdata?.slice?.() || [];
    return filterBy(datas, filter);
  };

  const filterChange = (event) => {
    setvatsData(filterData(event.filter));
  };
  const filtersupplier = (filter) => {
    const data = suppliersdata?.slice?.() || [];
    return filterBy(data, filter);
  };
  const filterChangesupplier = (event) => {
    setsuppliersdata(filtersupplier(event.filter));
  };

  const filterfeeearnerData = (filter) => {
    const data = feeearnerdata?.slice?.() || [];
    return filterBy(data, filter);
  };
  const filterfeeearner = (event) => {
    setfeeearnerData(filterfeeearnerData(event.filter));
  };
  const [nominaldata, setnominaldata] = useState([]);

  // Initialize expense nominals data when service loads
  useEffect(() => {
    if (expenseNominals?.length) {
      setnominaldata(expenseNominals.slice());
    }
  }, [expenseNominals]);

  const filternominalData = (filter) => {
    const data = expenseNominals?.slice?.() || [];
    return filterBy(data, filter);
  };
  const filternominal = (event) => {
    setnominaldata(filternominalData(event.filter));
  };

  const onChangevat = (event) => {
    setvatvalue(event.value);
  };
  const calculatevatamount = () => {
    if (vatvalue !== undefined && vatvalue !== null) {
      const vatamount = netamount * (vatvalue.rate / 100);
      setvatamount(vatamount);
    }
  };
  useEffect(() => {
    calculatevatamount();
  }, [vatvalue, netamount]);
  const onChangedateselect = (event) => {
    setdateselector(event.value);
  };

  const onChangefeeearner = (event) => {
    setfeeearnerref(event.value);
  };
  const onChangebranch = (event) => {
    setbranch(event.value);
  };
  const onChangenominal = (event) => {
    setnominal(event.value);
  };
  const onChangeofficevalue = (event) => {
    setofficevalue(event.value);
  };
  const onChangeclientvalue = (event) => {
    setclientvalue(event.value);
  };
  const onChangeClientBankFrom = (event) => {
    setClientBankFrom(event.value);
  };
  const onChangeClientBankTo = (event) => {
    setClientBankTo(event.value);
  };
  const onChangeOfficeBankTo = (event) => {
    setOfficeBankTo(event.value);
  };

  const filterOfficeBankData = (filter) => {
    const data = officeBankNominals?.slice?.() || [];
    return filterBy(data, filter);
  };
  const filterOfficeBank = (event) => {
    setOfficeBankData(filterOfficeBankData(event.filter));
  };

  const filterClientBankData = (filter) => {
    const data = clientBankNominals?.slice?.() || [];
    return filterBy(data, filter);
  };
  const filterClientBank = (event) => {
    setClientBankData(filterClientBankData(event.filter));
  };

  const filterBranchData = (filter) => {
    const data = branchesRaw?.slice?.() || [];
    return filterBy(data, filter);
  };
  const filterBranch = (event) => {
    setBranchesData(filterBranchData(event.filter));
  };

  const onChangematterTo = async (e) => {
    const selectedMatter = e.value;
    const selectedMatterId =
      typeof selectedMatter === "object" ? selectedMatter?.matterId : selectedMatter;
    if (selectedMatterId) {
      if (typeof selectedMatter === "object") {
        setMatterTo(selectedMatter);
      } else {
        setMatterTo((prev) =>
          prev?.matterId === selectedMatterId
            ? prev
            : { matterId: selectedMatterId },
        );
      }
      setMatterrefTo(selectedMatterId);
    } else {
      setMatterrefTo(null);
      setMatterTo(null);
    }
  };
  const onChangecounselfee = (event) => {
    setcounselfee(event.value);
  };
  const {
    files: filesChitRequest,
    addFile: addChitFile,
    clearFile: clearChitFile,
  } = useFiles(FILES_CONTEXT.CHIT_REQUEST);

  const handlesubmit = async (e) => {
    e.preventDefault();
    if (chitRequestReadOnly) return;
    setLoading(true);

    if (!type?.code) {
      setvalidation_message(true);
      setLoading(false);
      return;
    }

    const noMatterTypes = ["BP", "BR", "BT", "PIN"];

    if (IS_UNALLOCATED_CREATE) {
      const dateInvalid = !dateselector;
      const netInvalid = isChitNetAmountInvalid(netamount);
      const payeeSideInvalid = isPayeeSideInvalid(list, payee, payer);
      if (dateInvalid || netInvalid || payeeSideInvalid) {
        setvalidation_message(true);
        setLoading(false);
        return;
      }
    } else if (Object.values(validationErrors).some(Boolean)) {
      setvalidation_message(true);
      setLoading(false);
      return;
    }

    const feeEarnerRefResolved =
      feeearnerref?.userRef ??
      (typeof feeearnerref === "string" ? feeearnerref : undefined);

    let baseData = {
      postingType: type.code,
      reference: state,
      matterID: matterref,
      netAmount: netamount,
      feeEarnerRef: feeEarnerRefResolved,
      date: formatDateForAPI(dateselector),
      currencyCode: company?.baseCurrency,
      details: details,
      payee: payee,
      payer: payer,
      bankName: bankName,
      bankSortCode: sortCode,
      bankAccountNo: accountNumber,
      notes: notes,
      isCounselFee: counselfee,
      urgent: urgent,
      documentFiles: [],
      documentIDs: [],
    };

    if (filesChitRequest && filesChitRequest.length > 0) {
      const uploaded = filesChitRequest.filter((item) => !item.isPreAttached);
      const preAttached = filesChitRequest.filter((item) => item.isPreAttached);
      baseData.documentFiles = uploaded.map((item) => item.fileID);
      baseData.documentIDs = preAttached.map((item) => item.fileID);
    }

    let data = [baseData];
    switch (type.code) {
      case "ODR":
      case "OCR":
        baseData.officeBank = officevalue?.nominalRef;
        break;
      case "DPU":
        baseData.officeBank = officevalue?.nominalRef;
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        break;
      case "CDR":
      case "CCR":
        baseData.clientBank = clientvalue?.nominalRef;
        break;
      case "DDR":
      case "DCR":
        baseData.ddaBank = ddavalue?.nominalRef;
        break;
      case "DUU":
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        break;
      case "PIN":
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        baseData.supplierRef = suppliervalue?.supplierRef;
        baseData.branchId = branch?.branchId;
        baseData.nominalRef = nominal?.nominalRef;
        break;
      case "DIN":
        baseData.supplierRef = suppliervalue?.supplierRef;
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        break;
      case "CTC":
        baseData.matterIDTo = matterrefTo;
        baseData.clientBank = clientBankFrom?.nominalRef;
        baseData.clientBankTo = clientBankTo?.nominalRef;
        break;
      case "CTO":
      case "OTC":
        baseData.clientBank = clientvalue?.nominalRef;
        baseData.officeBank = officevalue?.nominalRef;
        break;
      case "BP":
        baseData.officeBank = officevalue?.nominalRef;
        baseData.nominalRef = nominal?.nominalRef;
        baseData.branchId = branch?.branchId;
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        break;
      case "BR":
        baseData.officeBank = officevalue?.nominalRef;
        baseData.nominalRef = nominal?.nominalRef;
        baseData.branchId = branch?.branchId;
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        break;
      case "BT":
        baseData.officeBank = officevalue?.nominalRef;
        baseData.officeBankTo = officeBankTo?.nominalRef;
        break;
      case "NTO":
      case "OTN":
      case "NTB":
        baseData.nominalRef = nominal?.nominalRef;
        break;
      default:
        baseData.vatAmount = vatamount;
        baseData.vatCode = vatvalue?.vatCodeId;
        baseData.vatRate = vatvalue?.rate;
        break;
    }

    if (!["PIN", "BP", "BR"].includes(data[0].postingType)) {
      data[0].branchId = undefined;
    }

    if (IS_MODAL && !noMatterTypes.includes(type.code)) {
      if (IS_UNALLOCATED_CREATE) {
        data[0].matterID = matterref ?? undefined;
        data[0].unallocated = true;
      } else {
        data[0].matterID = matterref || resolvedMatterId || undefined;
      }
    }

    if (noMatterTypes.includes(type.code)) {
      data[0].matterID = undefined;
    }

    // Check if we're in edit mode (chitRequestId exists) or create mode
    if (chitRequestId) {
      // Edit mode - use direct API call with toast
      try {
        setLoading(true);
        if (isRejectedChit) {
          data[0].repost = true;
        }
        if (unAllocated) {
          data[0].unallocated = false;
        }
        await updateChitRequest(chitRequestId, data[0]);
        toast.success("Chit request updated successfully!");
        props.setModal && props.setModal(false);
        const needsFullRefresh = isRejectedChit || unAllocated;
        props.cancelEdit &&
          props.cancelEdit(
            true,
            needsFullRefresh ? { fullRefresh: true } : undefined,
          );
      } catch (error) {
        console.error("Error updating chit request:", error);
      } finally {
        setLoading(false);
      }
      return; // Exit early for edit mode
    } else {
      try {
        await createChitRequestAPI(data);
        toast.success("Chit request created successfully!");
        if (IS_MODAL) {
          props.cancelEdit && props.cancelEdit(true);
        } else {
          resetfields();
        }
      } catch (error) {
        console.error("Error creating chit request:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const hasDeleteChitsAccess = useAccessRight(CaseAccessRights.DeleteChits);
  const resolvedStatusValue = String(chitStatus ?? "").toLowerCase();
  const isDeletableStatus =
    resolvedStatusValue === "pendingposting" ||
    resolvedStatusValue === "pendingauthorization" ||
    resolvedStatusValue === "rejected";
  const canDeleteChit =
    Boolean(chitRequestId) &&
    hasDeleteChitsAccess &&
    isDeletableStatus &&
    !chitRequestReadOnly;

  const handleDeleteChit = async () => {
    if (!canDeleteChit) return;
    try {
      setIsDeleting(true);
      await updateChitRequest(chitRequestId, { active: false });
      toast.success("Chit deleted successfully");
      setShowDeleteConfirm(false);
      props.cancelEdit(true, { fullRefresh: true });
    } catch (error) {
      console.error("Error deleting chit request:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRejectChit = async () => {
    if (chitRequestReadOnly) return;
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    try {
      setLoading(true);
      await rejectChitRequest(chitRequestId, { rejectReason: rejectionReason });
      toast.success("Chit request rejected successfully!");
      setShowRejectModal(false);
      setRejectionReason("");
      props.cancelEdit(true); // Pass true to indicate data was modified (rejected)
    } catch (error) {
      console.error("Error rejecting chit request:", error);
    } finally {
      setLoading(false);
    }
  };
  const resetfields = () => {
    // BatchPosting pattern: reset to initial defaults
    setvalidation_message(false);
    setstate("");
    setvatamount("");
    setpayee("");
    setpayer("");
    setdetails("");
    setBankName("");
    setSortCode("");
    setAccountNumber("");
    setNotes("");
    setnetamount(0);
    setdateselector(new Date());
    setcounselfee(false);
    seturgent(false);

    // Reset complex objects to initial state
    setvatvalue(null);
    setofficevalue(null);
    setclientvalue(null);
    setddavalue(null);
    setsuppliervalue(null);
    setnominal(null);
    setbranch({ branchId: "" });
    setfeeearnerref(null);
    setMatterrefTo(null);
    setMatterTo(null);
    setClientBankFrom(null);
    setClientBankTo(null);
    setOfficeBankTo(null);

    // Reset matter if not in modal mode (BatchPosting pattern)
    if (!IS_MODAL) {
      setmatterref(null);
      setMatter(null);
      setMatterBalanceClear(true);
      dispatch(clearMatterBalances("Chit Request"));
    }

    // Reset dropdown data to full lists
    if (suppliersdata?.length > 0) setsuppliersdata(suppliersdata.slice());
    if (activevatsdata?.length > 0) setvatsData(activevatsdata.slice());
    if (feeearnerdata?.length > 0) setfeeearnerData(feeearnerdata.slice());

    // Reset documents
    setresetDocs(!resetDocs);
    setFilesData([]);
    clearChitFile();
  };

  const { isSuccess, uploadForm, progress } = useUploadForm();

  const [filesData, setFilesData] = useState([]);

  useEffect(() => {
    if (chitRequestId) {
      const loadChitDocuments = async () => {
        try {
          const { data } = await getChitDocuments(chitRequestId);
          const docs = data?.data || [];
          const seeded = docs.map((d) => ({
            fileID: d.documentId,
            filename: d.documentName,
            isPreAttached: true,
          }));
          clearChitFile();
          setFilesData(seeded);
          seeded.forEach((f) => addChitFile(f));
        } catch (error) {
          console.error("Error loading chit documents:", error);
        }
      };
      loadChitDocuments();
      return;
    }
    if (!props.prefilledDocuments?.length) return;
    clearChitFile();
    const seeded = props.prefilledDocuments.map((d) => ({
      fileID: d.documentId,
      filename: d.documentName,
      isPreAttached: true,
    }));
    setFilesData(seeded);
    seeded.forEach((f) => addChitFile(f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chitRequestId]);

  const handleUpload = async (e) => {
    let data = onFileUpload(e);
    setFilesData([...filesData, data]);
    const parameters = {
      documentType: "Posting",
      matterId: matter?.matterId || "",
      supplierRef: suppliervalue?.supplierRef || "",
      nominalRef: nominal?.nominalRef || "",
    };
    const dataChoosen = await uploadForm(data, e.target.files[0], parameters);
    data.fileID = dataChoosen.data.data.fileID;
    addChitFile(dataChoosen);
  };
  const onFileRemove = (fileId) => {
    setFilesData((prev) => prev.filter((file) => file.fileID !== fileId));
  };

  const comboBoxesInfo = [
    {
      key: "SupplierRef",
      label: "Supplier Ref",
      condition: !suppliervalue?.supplierRef,
      data: suppliers,
      onFilterChange: filterChangesupplier,
      value: suppliervalue,
      onChange: onChangesupplier,
      field: "display",
      placeholder: "Search Supplier Ref",
    },
    {
      key: "NominalRef",
      label: "Nominal Ref",
      condition: !nominal?.nominalRef,
      data: nominaldata,
      onFilterChange: filternominal,
      value: nominal,
      onChange: onChangenominal,
      placeholder: "Search Nominal Ref",
    },
    {
      key: "OfficeBank",
      label: "Office Bank",
      condition: !officevalue?.nominalRef,
      data: officeBankData,
      onFilterChange: filterOfficeBank,
      value: officevalue || null,
      onChange: onChangeofficevalue,
      field: "display",
      placeholder: "Search Office Bank",
    },
    {
      key: "ClientBank",
      label: "Client Bank",
      condition: !clientvalue,
      data: clientBankData,
      onFilterChange: filterClientBank,
      value: clientvalue || null,
      onChange: onChangeclientvalue,
      field: "display",
      placeholder: "Search Client Bank",
    },
    {
      key: "ClientBankFrom",
      label: "From Client Bank",
      condition: !clientBankFrom,
      data: clientBankData,
      onFilterChange: filterClientBank,
      value: clientBankFrom,
      onChange: onChangeClientBankFrom,
      field: "display",
      placeholder: "Search From Client Bank",
    },
    {
      key: "ClientBankTo",
      label: "To Client Bank",
      condition: !clientBankTo,
      data: clientBankData,
      onFilterChange: filterClientBank,
      value: clientBankTo,
      onChange: onChangeClientBankTo,
      field: "display",
      placeholder: "Search To Client Bank",
    },
    {
      key: "OfficeBankTo",
      label: "To Bank",
      condition: !officeBankTo,
      data: officeBankData,
      onFilterChange: filterOfficeBank,
      value: officeBankTo,
      onChange: onChangeOfficeBankTo,
      field: "display",
      placeholder: "Search To Bank",
    },
  ];

  const validationErrors = getChitValidationErrors({
    type,
    list,
    unAllocated,
    matterref,
    matterrefTo,
    dateselector,
    payee,
    payer,
    netamount,
    vatvalue,
    feeearnerref,
    comboBoxesInfo,
    branchValue: branch?.branchId ? branch : null,
    details,
    reference: state,
  });

  const submitButtonText = chitRequestId
    ? isRejectedChit
      ? "Repost"
      : "Update"
    : "Post";
  const isFormReady = postingTypesLoaded && !isLoadingChitData;

  const contentProps = {
    IS_MODAL,
    IS_UNALLOCATED_CREATE,
    isFormReady,
    isFetchingChit: isLoadingChitData,
    handlesubmit,
    postingTypes,
    type,
    handleChange,
    chitRequestId,
    onChangematter,
    canSelectMatterInModal,
    matterref,
    unAllocated,
    matter,
    resolvedMatterProp: props.matter,
    dateselector,
    onChangedateselect,
    matterBalanceClear,
    list,
    state,
    setstate,
    payee,
    setpayee,
    payer,
    setpayer,
    netamount,
    setnetamount,
    vatvalue,
    calculatevatamount,
    vatsdata,
    filterChange,
    onChangevat,
    vatamount,
    feeearner,
    filterfeeearner,
    feeearnerref,
    onChangefeeearner,
    comboBoxesInfo,
    bankName,
    setBankName,
    sortCode,
    setSortCode,
    accountNumber,
    setAccountNumber,
    details,
    setdetails,
    counselfee,
    setcounselfee,
    urgent,
    seturgent,
    resetDocs,
    progress,
    filesData,
    onFileRemove,
    handleUpload,
    setFilesData,
    notes,
    setNotes,
    isRejectedChit,
    rejectionInfo,
    resetfields,
    loading,
    validation_message,
    isChitNetAmountInvalid,
    validationErrors,
    readOnly: chitRequestReadOnly,
    matterTo,
    matterrefTo,
    onChangematterTo,
    branchValue: branch?.branchId ? branch : null,
    onChangeBranch: onChangebranch,
    branchesData,
    filterBranch,
  };

  return (
    <>
      <ChitRequestRejectDialog
        open={showRejectModal}
        loading={loading}
        rejectionReason={rejectionReason}
        onReasonChange={(e) => setRejectionReason(e.value)}
        onClose={() => {
          setShowRejectModal(false);
          setRejectionReason("");
        }}
        onReject={handleRejectChit}
      />

      {showDeleteConfirm && (
        <ConfirmationDialog
          content="Are you sure you want to delete this chit?"
          onClickYes={handleDeleteChit}
          onClickNo={() => setShowDeleteConfirm(false)}
          onClose={() => !isDeleting && setShowDeleteConfirm(false)}
          isLoading={isDeleting}
        />
      )}

      {IS_MODAL ? (
        <Dialog
          title={
            chitRequestId
              ? "Edit Chit Request"
              : viewMode === "unallocated" || IS_UNALLOCATED_CREATE
                ? "Chit Posting - Unallocated"
                : "Chit Request"
          }
          onClose={() => props.cancelEdit(false)}
          className="dialog-xl">
          <ChitRequestFormContent {...contentProps} />
          <DialogActionsBar>
            <div className={`d-flex justify-content-between w-100 dialog-footer`}>
              <div className="d-flex gap-2 ms-4">
                {chitRequestId && !isRejectedChit && !chitRequestReadOnly && (
                  <Button
                    className="common-btn bgBtnSecondary"
                    type="button"
                    onClick={() => setShowRejectModal(true)}
                    disabled={loading}>
                    Reject Chit
                  </Button>
                )}
                {canDeleteChit && (
                  <Button
                    className="common-btn bgBtnSecondary"
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={loading || isDeleting}>
                    Delete Chit
                  </Button>
                )}
              </div>
              <div>
                <Button
                  className="common-btn bgBtnSecondary me-3"
                  type="button"
                  onClick={() => {
                    resetfields();
                    if (IS_MODAL) props.cancelEdit(false); // Pass false when cancelling without changes
                  }}>
                  Cancel
                </Button>
                <SubmitButton
                  className="me-3"
                  id="chit-request-post"
                  type="submit"
                  onClick={handlesubmit}
                  isLoading={loading}
                  disabled={loading || chitRequestReadOnly}>
                  {submitButtonText}
                </SubmitButton>
              </div>
            </div>
          </DialogActionsBar>
        </Dialog>
      ) : (
        <ChitRequestFormContent {...contentProps} />
      )}
    </>
  );
};
export default ChitRequest;
